import { UseFormReturn } from 'react-hook-form';
import { SurveyFormData } from '@/utils/validation';
import CustomerDetailsSection from './CustomerDetailsSection';
import SatisfactionRatingsSection from './SatisfactionRatingsSection';
import OpenEndedSection from './OpenEndedSection';
import ConsentSection from './ConsentSection';

interface SurveyFormProps {
  form: UseFormReturn<SurveyFormData>;
  onSubmit: (data: SurveyFormData) => Promise<void>;
  isSubmitting: boolean;
}

const SurveyForm = ({ form, onSubmit, isSubmitting }: SurveyFormProps) => {
  const watchedValues = form.watch();
  
  // Count completed fields across nested objects
  const countCompletedFields = (obj: unknown): number => {
    if (obj === null || obj === undefined) return 0;
    if (typeof obj !== 'object') {
      return (obj !== '' && obj !== false && obj !== undefined) ? 1 : 0;
    }
    return Object.values(obj).reduce((sum: number, val) => sum + countCompletedFields(val), 0);
  };
  
  const totalFields = 18; // Total required fields
  const completedFields = countCompletedFields(watchedValues);
  const progress = Math.min((completedFields / totalFields) * 100, 100);

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="px-6 py-8 space-y-10"
      noValidate
      aria-label="Customer Satisfaction Survey Form"
    >
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Form Progress</span>
          <span className="text-sm font-medium text-primary-600">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-gradient-to-r from-primary-500 to-primary-600 h-2.5 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <CustomerDetailsSection form={form} />
      <SatisfactionRatingsSection form={form} />
      <OpenEndedSection form={form} />
      <ConsentSection form={form} />

      <div className="flex justify-between items-center pt-8 border-t-2 border-gray-200 bg-gray-50 -mx-6 px-6 py-6 rounded-b-2xl">
        <p className="text-sm text-gray-600">
          * Required fields
        </p>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center py-3 px-8 border border-transparent shadow-lg text-base font-semibold rounded-lg text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition hover:scale-105 active:scale-95"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Submitting...
            </>
          ) : (
            <>
              <svg
                className="mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Submit Survey
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default SurveyForm;

